import React from 'react'

import { Stack, Typography, useTheme } from '@mui/material'
import { Button } from '@repo/ui'
import { CircleCheck } from 'lucide-react'

type Props = {
  /** Row label, e.g. "Email address" or "Phone number". */
  label: string
  /** The address itself, when there is one. */
  value?: string | null
  /** Shows a check next to the action. Only pass when the app really knows. */
  verified?: boolean
  /** Sub-line shown when there is no value, or when it is not yet verified. */
  emptyHint: string
  unverifiedHint?: string
  actionLabel: string
  onAction: () => void
}

/**
 * One "which address does this channel go to?" row: label + address on the
 * left, verification state + an action on the right.
 *
 * Extracted on its second hand-roll (working-conventions §36) — first rolled
 * for the phone row, then needed identically for the email row once VERA-842
 * made username the stable identifier and email a changeable, per-channel
 * address rather than the account's identity.
 */
export const ChannelAddressRow = ({
  label,
  value,
  verified,
  emptyHint,
  unverifiedHint,
  actionLabel,
  onAction,
}: Props) => {
  const theme = useTheme()

  const description = !value
    ? emptyHint
    : verified === false && unverifiedHint
      ? unverifiedHint
      : value

  return (
    <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ py: 1 }}>
      <Stack sx={{ pr: 3 }}>
        <Typography sx={{ color: theme.palette.text.primary, fontSize: '16px' }}>
          {label}
        </Typography>
        <Typography variant='body2' sx={{ color: theme.palette.text.secondary }}>
          {description}
        </Typography>
      </Stack>
      <Stack direction='row' alignItems='center' spacing={3}>
        {verified && (
          <CircleCheck size={20} strokeWidth={1.5} color={(theme.palette as any).success?.main || '#2e7d32'} />
        )}
        <Button variant='secondary' onClick={onAction}>
          {actionLabel}
        </Button>
      </Stack>
    </Stack>
  )
}
